/**
 * [C38] Settings → النظام → "صيانة النظام" (B6 part 2; original/src/App.tsx:16766-17240 → `MaintenanceCardBody`).
 * Binds the card to the office API and runs the browser side of the backup key (D-173, D-208, D-209):
 *  - key wizard: the key pair is generated **here**, the recovery sheet printed, the public key registered (PIN +
 *    a fresh authenticator code), then the sheet is proven by a test decrypt (the server's sealed challenge opened
 *    with the key typed back from paper); replacing a key asks why (rotated / lost, D-194);
 *  - the optional platform share (D-208, off by default): the key is split 2-of-3 here — device, sheet, platform;
 *  - downloads (D-151): a full export now, or a kept copy (nightly, key rotation, the copy before a reset);
 *  - dataset versions (D-062): read-only view of a superseded version, rollback while nothing was posted since;
 *  - factory reset (D-051, D-155): three warnings, the office name typed, a fresh code, the PIN; the server makes
 *    the copy first. The private key and shares live only in this component's state and are wiped on close.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MaintenanceCardBody, MaintenanceDialog, SettingsCardFrame, type MaintenanceBackupView, type MaintenanceDatasetView } from "@faylasuf/ui";
import type { BackupState, DatasetState } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { download, usePrintJob } from "../../app/documents.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";
import { clearDeviceCopy, combine, fromB64, generateKeyPair, keyPairFromPrivate, loadDeviceCopy, openChallenge, readSheet, saveDeviceCopy, sheetText, split2of3, toB64, type BrowserKeyPair } from "../../app/backup-key.js";
import { BackupSheet } from "./BackupSheet.js";

type T = (k: string, p?: Record<string, string | number>) => string;
type Dialog = { kind: "create" } | { kind: "replace" } | { kind: "verify" } | { kind: "share" } | { kind: "reset" } | { kind: "rollback"; version: number; discards: { customers: number; settings: number; users: number } } | null;

const INPUT = "w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-sm font-bold dark:text-[#f0f6fc]";
const CODE_INPUT = `${INPUT} font-mono text-center tracking-widest`;
const NOTE = "p-4 bg-amber-50 dark:bg-amber-950/20 rounded-2xl text-xs font-bold text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900/30 leading-relaxed";
const PRIMARY = "w-full py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all disabled:opacity-50";
const SECONDARY = "w-full py-3 bg-gray-200 dark:bg-[#0d1117] hover:bg-gray-300 dark:hover:bg-[#161b22] text-gray-700 dark:text-[#f0f6fc] rounded-xl font-black text-xs uppercase tracking-widest transition-all disabled:opacity-50";

const size = (n: number) => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const sheetErr = (t: T, e: unknown) => ((e as Error).message?.startsWith("SHEET_") || (e as Error).message?.startsWith("SHARE_") ? t("maint.sheet_typo") : null);

function TotpField({ value, onChange, t }: { value: string; onChange: (v: string) => void; t: T }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-gray-400 dark:text-[#8b949e] mb-1 uppercase tracking-widest">{t("maint.totp")}</label>
      <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} className={CODE_INPUT} data-testid="maint-totp" />
    </div>
  );
}

function SheetField({ value, onChange, t, label }: { value: string; onChange: (v: string) => void; t: T; label?: string }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-gray-400 dark:text-[#8b949e] mb-1 uppercase tracking-widest">{label ?? t("maint.type_sheet")}</label>
      <textarea dir="ltr" rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={`${INPUT} font-mono`} spellCheck={false} autoComplete="off" data-testid="maint-sheet-input" />
    </div>
  );
}

function Err({ text }: { text: string | null }) {
  return text ? <p className="text-xs font-bold text-rose-600" data-testid="maint-error">{text}</p> : null;
}

export function MaintenanceContainer({ customIcon }: { customIcon?: string }) {
  const { t, source } = useApp();
  const { session, isSuperManager, hasPermission, sessionLost } = useSession();
  const canDownload = hasPermission("download_backup");
  const qc = useQueryClient();
  const toast = useToast();
  const pin = usePinGate();
  const print = usePrintJob();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const tz = session.office.timeZone;
  const fmt = (iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso)).replace(",", "");
  const fmtDay = (iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));

  const bq = useQuery({ queryKey: keys.backup, queryFn: () => source.backupState(), enabled: isSuperManager || canDownload });
  const running = (s: DatasetState | undefined) => s?.request?.status === "queued" || s?.request?.status === "running";
  const dq = useQuery({ queryKey: keys.datasets, queryFn: () => source.datasets(), enabled: isSuperManager, refetchInterval: (q) => (running(q.state.data as DatasetState | undefined) ? 5000 : false) });
  if ((bq.error && isSessionLost(bq.error)) || (dq.error && isSessionLost(dq.error))) sessionLost();

  // a reset / rollback that finished replaced the session's database: sign in again (the old sessions went with it)
  const lastStatus = useRef<string | null>(null);
  useEffect(() => {
    const r = dq.data?.request;
    const prev = lastStatus.current;
    lastStatus.current = r ? `${r.id}:${r.status}` : null;
    if (!r || !prev || prev === lastStatus.current || !prev.startsWith(r.id)) return;
    if (r.status === "done") sessionLost();
    else if (r.status === "refused" || r.status === "failed") toast(t(`error.${String(r.result?.["code"] ?? "DATASET_REQUEST_FAILED")}`), "error");
  }, [dq.data?.request?.id, dq.data?.request?.status]);

  const b = bq.data;
  const d = dq.data;
  const backupView: MaintenanceBackupView | null = b
    ? {
        configured: b.configured,
        key: b.key ? { keyId: b.key.keyId, since: fmtDay(b.key.createdAt), verifiedAt: b.key.verifiedAt ? fmtDay(b.key.verifiedAt) : null } : null,
        platformShare: b.platformShare ? { since: fmtDay(b.platformShare.since) } : null,
        copies: b.copies.map((c) => ({ id: c.id, label: `${t(`maint.copy.${c.kind}`)} — ${t(`maint.purpose.${c.purpose}`)}${c.datasetVersion > 1 || c.purpose === "reset" ? ` · ${t("maint.version", { n: c.datasetVersion })}` : ""}`, createdAt: fmt(c.createdAt), size: size(c.bytes), available: c.available, highlight: c.purpose === "reset" })),
      }
    : null;
  const requestText = (r: DatasetState["request"]): MaintenanceDatasetView["request"] => {
    if (!r) return null;
    if (r.status === "queued" || r.status === "running") return { text: t(`maint.request.${r.kind}_running`), tone: "busy" };
    if (r.status === "done") return { text: t(`maint.request.${r.kind}_done`, { date: fmt(r.finishedAt ?? r.requestedAt) }), tone: "ok" };
    return { text: `${t(`maint.request.${r.kind}_refused`)} — ${t(`error.${String(r.result?.["code"] ?? "DATASET_REQUEST_FAILED")}`)}`, tone: "error" };
  };
  const datasetView: MaintenanceDatasetView | null = d
    ? {
        currentVersion: d.currentVersion,
        versions: d.versions.map((v) => ({
          version: v.version,
          current: v.status === "current",
          status: t(`maint.v_status.${v.status}`),
          detail: v.status === "current" ? t("maint.v_current", { date: fmtDay(v.createdAt) }) : v.viewable ? t("maint.v_viewable", { date: fmtDay(v.viewableUntil!) }) : t("maint.v_archived"),
          viewable: v.viewable,
        })),
        viewing: d.viewing,
        rollback: d.rollback,
        request: requestText(d.request),
        resetBlocked: running(d) ? t("maint.request_running") : !d.reset.exportVolume ? t("maint.not_configured") : !d.reset.backupKey ? t("maint.reset_needs_key") : d.reset.envelopes > 0 ? t("maint.reset_envelopes", { n: d.reset.envelopes }) : null,
      }
    : null;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: keys.backup });
    await qc.invalidateQueries({ queryKey: keys.datasets });
  };
  const guard = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
    } finally {
      setBusy(false);
    }
  };

  const onDownloadNow = () =>
    guard(async () => {
      const blob = await pin.withPin((p) => source.backupDownload(p.pin!), { message: t("maint.download_pin") });
      if (!blob) return;
      download(blob, `faylasuf-export-${session.office.code}-${new Date().toISOString().slice(0, 10)}.fsx`);
      await refresh();
    });
  const onDownloadCopy = (id: string) =>
    guard(async () => {
      const c = b?.copies.find((x) => x.id === id);
      const blob = await pin.withPin((p) => source.backupCopyDownload(id, p.pin!), { message: t("maint.download_pin") });
      if (blob && c) download(blob, c.fileName);
    });
  const onView = (version: number) =>
    guard(async () => {
      const r = await pin.withPin((p) => source.datasetView(version, p.pin!), { message: t("maint.view_pin", { n: version }) });
      if (r) await qc.invalidateQueries();
    });
  const onCloseView = () =>
    guard(async () => {
      await source.datasetViewClose();
      await qc.invalidateQueries();
    });

  return (
    <SettingsCardFrame cardKey="maintenance" {...(customIcon ? { customIcon } : {})}>
      <MaintenanceCardBody
        isSuperManager={isSuperManager}
        canDownload={canDownload}
        backup={backupView}
        datasets={datasetView}
        busy={busy}
        onCreateKey={() => setDialog({ kind: "create" })}
        onReplaceKey={() => setDialog({ kind: "replace" })}
        onVerifyKey={() => setDialog({ kind: "verify" })}
        onShare={() => setDialog({ kind: "share" })}
        onDownloadNow={() => void onDownloadNow()}
        onDownloadCopy={(id) => void onDownloadCopy(id)}
        onView={(v) => void onView(v)}
        onCloseView={() => void onCloseView()}
        onRollback={(v) => setDialog({ kind: "rollback", version: v, discards: d?.rollback?.discards ?? { customers: 0, settings: 0, users: 0 } })}
        onReset={() => setDialog({ kind: "reset" })}
      />
      {(dialog?.kind === "create" || dialog?.kind === "replace") && <KeyWizard replace={dialog.kind === "replace"} state={b} onClose={() => setDialog(null)} onDone={refresh} pin={pin} print={print} t={t} />}
      {dialog?.kind === "verify" && b?.key && <VerifyDialog state={b} onClose={() => setDialog(null)} onDone={refresh} t={t} />}
      {dialog?.kind === "share" && b?.key && <ShareDialog state={b} onClose={() => setDialog(null)} onDone={refresh} pin={pin} print={print} t={t} />}
      {dialog?.kind === "reset" && <ResetDialog onClose={() => setDialog(null)} onDone={refresh} pin={pin} t={t} />}
      {dialog?.kind === "rollback" && <RollbackDialog version={dialog.version} discards={dialog.discards} onClose={() => setDialog(null)} onDone={refresh} pin={pin} t={t} />}
      {pin.dialog}
      {print.portal}
    </SettingsCardFrame>
  );
}

type PinGate = ReturnType<typeof usePinGate>;
type PrintJob = ReturnType<typeof usePrintJob>;

/** The sheet print and the device copy, shared by the wizard and the share dialog. */
function useSheet(print: PrintJob, t: T) {
  const { session } = useSession();
  return (kind: "key" | "share", keyId: string, text: string) =>
    print.start(<BackupSheet id="backup-sheet" kind={kind} keyId={keyId} text={text} officeName={session.office.name} officeCode={session.office.code} createdAt={new Intl.DateTimeFormat("en-CA", { timeZone: session.office.timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date()).replace(",", "")} t={t} />, "backup-sheet", "a4");
}

// ---------------------------------------------------------------------------------------------------------------
// Key wizard (D-173, D-209): generate → sheet → register → test decrypt
// ---------------------------------------------------------------------------------------------------------------

function KeyWizard({ replace, state, onClose, onDone, pin, print, t }: { replace: boolean; state: BackupState | undefined; onClose: () => void; onDone: () => Promise<void>; pin: PinGate; print: PrintJob; t: T }) {
  const { source } = useApp();
  const { sessionLost } = useSession();
  const [step, setStep] = useState<"intro" | "sheet" | "register" | "verify" | "done">("intro");
  const [reason, setReason] = useState<"rotated" | "lost">("rotated");
  const [kp, setKp] = useState<BrowserKeyPair | null>(null);
  const [printed, setPrinted] = useState(false);
  const [totp, setTotp] = useState("");
  const [typed, setTyped] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const sheet = useSheet(print, t);
  useEffect(() => () => void kp?.privateKey.fill(0), [kp]); // the private key leaves memory with the dialog
  const text = useMemo(() => (kp ? sheetText.key(kp.privateKey) : ""), [kp]);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else setErr(sheetErr(t, e) ?? errorMessage(t, e));
    } finally {
      setBusy(false);
    }
  };
  let body: ReactNode;
  if (step === "intro")
    body = (
      <>
        <div className={NOTE}>{replace ? t("maint.wizard.replace_intro") : t("maint.wizard.intro")}</div>
        {replace && (
          <div className="flex gap-2">
            {(["rotated", "lost"] as const).map((r) => (
              <button key={r} type="button" onClick={() => setReason(r)} className={`flex-1 py-2 rounded-xl text-xs font-black border ${reason === r ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#30363d] text-gray-600 dark:text-[#8b949e]"}`}>
                {t(`maint.reason.${r}`)}
              </button>
            ))}
          </div>
        )}
        {replace && state?.platformShare && <p className="text-[11px] text-gray-600 dark:text-[#8b949e]">{t("maint.wizard.replace_share")}</p>}
        <button type="button" className={PRIMARY} disabled={busy} onClick={() => void run(async () => (setKp(await generateKeyPair()), setStep("sheet")))} data-testid="maint-generate">
          {t("maint.wizard.generate")}
        </button>
      </>
    );
  else if (step === "sheet")
    body = (
      <>
        <div className={NOTE}>{t("maint.wizard.sheet_note")}</div>
        <div dir="ltr" className="p-4 rounded-2xl border-2 border-gray-900 dark:border-[#30363d] font-mono text-sm leading-8 text-left break-all" data-testid="maint-key-text">
          {text}
        </div>
        <button type="button" className={SECONDARY} onClick={() => sheet("key", kp!.keyId, text)} data-testid="maint-print-sheet">
          {t("maint.wizard.print")}
        </button>
        <label className="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-[#f0f6fc]">
          <input type="checkbox" checked={printed} onChange={(e) => setPrinted(e.target.checked)} data-testid="maint-printed" />
          {t("maint.wizard.printed")}
        </label>
        <button type="button" className={PRIMARY} disabled={!printed} onClick={() => setStep("register")}>
          {t("maint.next")}
        </button>
      </>
    );
  else if (step === "register")
    body = (
      <>
        <div className={NOTE}>{t("maint.wizard.register_note")}</div>
        <TotpField value={totp} onChange={setTotp} t={t} />
        <Err text={err} />
        <button
          type="button"
          className={PRIMARY}
          disabled={busy || totp.length !== 6}
          data-testid="maint-register"
          onClick={() =>
            void run(async () => {
              const r = await pin.withPin((p) => source.backupRegisterKey(toB64(kp!.publicKey), totp, replace ? reason : undefined, p.pin!), { message: t("maint.wizard.register_pin") });
              if (!r) return;
              // [C38 review 5] the whole key is never kept in the browser: the sheet (and the helper, B7) hold it
              await onDone();
              setStep("verify");
            })
          }
        >
          {t("maint.wizard.register")}
        </button>
      </>
    );
  else if (step === "verify")
    body = (
      <>
        <div className={NOTE}>{t("maint.wizard.verify_note")}</div>
        <SheetField value={typed} onChange={setTyped} t={t} />
        <Err text={err} />
        <button type="button" className={PRIMARY} disabled={busy || typed.trim().length < 20} onClick={() => void run(async () => (await testDecrypt(source, typed, null, kp!.keyId), await onDone(), setStep("done")))} data-testid="maint-verify">
          {t("maint.verify_key")}
        </button>
      </>
    );
  else
    body = (
      <>
        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl text-xs font-bold text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30" data-testid="maint-done">
          {t("maint.wizard.done")}
        </div>
        <button type="button" className={SECONDARY} onClick={onClose}>
          {t("maint.close")}
        </button>
      </>
    );
  return (
    <MaintenanceDialog title={replace ? t("maint.replace_key") : t("maint.create_key")} onClose={onClose} wide testId="maint-key-wizard">
      {body}
    </MaintenanceDialog>
  );
}

/** D-209: the key (or two shares) typed from paper opens the server's sealed challenge. */
async function testDecrypt(source: ReturnType<typeof useApp>["source"], typed: string, deviceShare: ReturnType<typeof readSheet> | null, keyId: string): Promise<void> {
  const s = readSheet(typed);
  let priv: Uint8Array;
  if (s.kind === "key") priv = s.privateKey;
  else if (deviceShare?.kind === "share") priv = combine(s.share, deviceShare.share);
  else throw new Error("SHARE_FORMAT");
  const kp = await keyPairFromPrivate(priv);
  try {
    if (kp.keyId !== keyId) throw new Error("SHEET_KEY"); // a valid text, but not this office's key
    const ch = await source.backupKeyCheck();
    let nonce: Uint8Array;
    try {
      nonce = await openChallenge(ch.sealed, kp);
    } catch {
      nonce = new Uint8Array(32); // a key that does not open it: the server counts the failed attempt
    }
    await source.backupKeyVerify(ch.checkId, toB64(nonce));
  } finally {
    priv.fill(0);
  }
}

function VerifyDialog({ state, onClose, onDone, t }: { state: BackupState; onClose: () => void; onDone: () => Promise<void>; t: T }) {
  const { source } = useApp();
  const { session, sessionLost } = useSession();
  const [typed, setTyped] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const keyId = state.key!.keyId;
  return (
    <MaintenanceDialog title={t("maint.verify_key")} onClose={onClose} testId="maint-verify-dialog">
      <div className={NOTE}>{state.platformShare ? t("maint.verify_note_share") : t("maint.wizard.verify_note")}</div>
      <SheetField value={typed} onChange={setTyped} t={t} />
      <Err text={err} />
      {ok ? (
        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl text-xs font-bold text-emerald-700 border border-emerald-100" data-testid="maint-done">
          {t("maint.verified")}
        </div>
      ) : (
        <button
          type="button"
          className={PRIMARY}
          disabled={busy || typed.trim().length < 20}
          onClick={() => {
            setBusy(true);
            setErr(null);
            void (async () => {
              try {
                const device = state.platformShare ? await loadDeviceCopy(session.office.code, keyId) : null;
                if (state.platformShare && device?.kind !== "share") throw new Error("DEVICE_SHARE_MISSING");
                await testDecrypt(source, typed, device, keyId);
                await onDone();
                setOk(true);
              } catch (e) {
                if (isSessionLost(e)) sessionLost();
                else setErr((e as Error).message === "DEVICE_SHARE_MISSING" ? t("maint.device_share_missing") : (sheetErr(t, e) ?? errorMessage(t, e)));
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          {t("maint.verify_key")}
        </button>
      )}
    </MaintenanceDialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// D-208 platform share: on (split 2-of-3), off (back to the whole key), recover (sheet share + platform share)
// ---------------------------------------------------------------------------------------------------------------

function ShareDialog({ state, onClose, onDone, pin, print, t }: { state: BackupState; onClose: () => void; onDone: () => Promise<void>; pin: PinGate; print: PrintJob; t: T }) {
  const { source } = useApp();
  const { session, sessionLost } = useSession();
  const on = !!state.platformShare;
  const keyId = state.key!.keyId;
  const [mode, setMode] = useState<"enable" | "disable" | "recover">(on ? "recover" : "enable");
  const [typed, setTyped] = useState("");
  const [totp, setTotp] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ kind: "key" | "share"; text: string } | null>(null);
  const sheet = useSheet(print, t);
  /** The whole key: the typed full key, or (share on) the typed sheet share + this device's share. */
  const assemble = async (): Promise<Uint8Array> => {
    const device = await loadDeviceCopy(session.office.code, keyId);
    const s = readSheet(typed);
    if (s.kind === "key") return s.privateKey;
    if (device?.kind !== "share") throw new Error("DEVICE_SHARE_MISSING");
    return combine(s.share, device.share);
  };
  const go = () => {
    setBusy(true);
    setErr(null);
    void (async () => {
      let priv: Uint8Array | null = null;
      try {
        if (mode === "enable") {
          priv = await assemble();
          if ((await keyPairFromPrivate(priv)).keyId !== keyId) throw new Error("SHEET_KEY");
          const [dev, paper, platform] = split2of3(priv);
          const r = await pin.withPin((p) => source.backupShareEnable(keyId, toB64(Uint8Array.from([platform!.x, ...platform!.y])), totp, p.pin!), { message: t("maint.share.pin") });
          if (!r) return;
          await saveDeviceCopy(session.office.code, keyId, "share", Uint8Array.from([dev!.x, ...dev!.y]));
          setResult({ kind: "share", text: sheetText.share(paper!) });
        } else if (mode === "disable") {
          priv = await assemble();
          if ((await keyPairFromPrivate(priv)).keyId !== keyId) throw new Error("SHEET_KEY");
          const r = await pin.withPin((p) => source.backupShareRemove(totp, p.pin!), { message: t("maint.share.pin") });
          if (!r) return;
          await clearDeviceCopy(session.office.code);
          setResult({ kind: "key", text: sheetText.key(priv) });
        } else {
          const s = readSheet(typed);
          if (s.kind !== "share") throw new Error("SHARE_FORMAT");
          const r = await pin.withPin((p) => source.backupShareRelease(totp, p.pin!), { message: t("maint.share.pin") });
          if (!r) return;
          const raw = fromB64(r.share);
          priv = combine(s.share, { x: raw[0]!, y: raw.slice(1) });
          raw.fill(0);
          if ((await keyPairFromPrivate(priv)).keyId !== keyId) throw new Error("SHEET_KEY");
          setResult({ kind: "key", text: sheetText.key(priv) });
        }
        await onDone();
      } catch (e) {
        const m = (e as Error).message;
        if (isSessionLost(e)) sessionLost();
        else setErr(m === "DEVICE_SHARE_MISSING" ? t("maint.device_share_missing") : m === "SHEET_KEY" ? t("error.BACKUP_KEY_CHECK_FAILED") : (sheetErr(t, e) ?? errorMessage(t, e)));
      } finally {
        priv?.fill(0);
        setBusy(false);
      }
    })();
  };
  return (
    <MaintenanceDialog title={t("maint.share.title")} onClose={onClose} wide testId="maint-share-dialog">
      <div className={NOTE}>{t("maint.share.explain")}</div>
      {result ? (
        <>
          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl text-xs font-bold text-emerald-700 border border-emerald-100">{t(`maint.share.done_${mode}`)}</div>
          <div dir="ltr" className="p-4 rounded-2xl border-2 border-gray-900 dark:border-[#30363d] font-mono text-sm leading-8 text-left break-all">
            {result.text}
          </div>
          <button type="button" className={SECONDARY} onClick={() => sheet(result.kind, keyId, result.text)}>
            {t("maint.wizard.print")}
          </button>
        </>
      ) : (
        <>
          {on && (
            <div className="flex gap-2">
              {(["recover", "disable"] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={`flex-1 py-2 rounded-xl text-xs font-black border ${mode === m ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 dark:border-[#30363d] text-gray-600 dark:text-[#8b949e]"}`}>
                  {t(`maint.share.mode_${m}`)}
                </button>
              ))}
            </div>
          )}
          <SheetField value={typed} onChange={setTyped} t={t} label={mode === "enable" ? t("maint.share.type_key_optional") : t("maint.share.type_share")} />
          <TotpField value={totp} onChange={setTotp} t={t} />
          <Err text={err} />
          <button type="button" className={PRIMARY} disabled={busy || totp.length !== 6} onClick={go} data-testid="maint-share-go">
            {t(`maint.share.go_${mode}`)}
          </button>
        </>
      )}
    </MaintenanceDialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Factory reset (D-051, D-155) and rollback (D-062)
// ---------------------------------------------------------------------------------------------------------------

function ResetDialog({ onClose, onDone, pin, t }: { onClose: () => void; onDone: () => Promise<void>; pin: PinGate; t: T }) {
  const { source } = useApp();
  const { session, sessionLost } = useSession();
  const [ack, setAck] = useState([false, false, false]);
  const [name, setName] = useState("");
  const [totp, setTotp] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = ack.every(Boolean) && name.trim().length > 0 && totp.length === 6;
  return (
    <MaintenanceDialog title={t("maint.reset_title")} onClose={onClose} wide testId="maint-reset-dialog">
      {[0, 1, 2].map((i) => (
        <label key={i} className="flex items-start gap-2 p-3 bg-rose-50 dark:bg-rose-900/10 rounded-xl border border-rose-100 dark:border-rose-900/30 text-[11px] font-bold text-rose-700 dark:text-rose-400 leading-relaxed">
          <input type="checkbox" className="mt-0.5" checked={ack[i]} onChange={(e) => setAck(ack.map((v, j) => (j === i ? e.target.checked : v)))} data-testid={`maint-ack-${i}`} />
          {t(`maint.reset_ack_${i}`)}
        </label>
      ))}
      <div>
        <label className="block text-[10px] font-bold text-gray-400 dark:text-[#8b949e] mb-1 uppercase tracking-widest">{t("maint.reset_type_name", { name: session.office.name })}</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} autoComplete="off" data-testid="maint-reset-name" />
      </div>
      <TotpField value={totp} onChange={setTotp} t={t} />
      <Err text={err} />
      <button
        type="button"
        className={PRIMARY}
        disabled={busy || !ready}
        data-testid="maint-reset-go"
        onClick={() => {
          setBusy(true);
          setErr(null);
          void (async () => {
            try {
              const r = await pin.withPin((p) => source.datasetReset(name, totp, p.pin!), { message: t("maint.reset_pin") });
              if (!r) return;
              await onDone();
              onClose();
            } catch (e) {
              if (isSessionLost(e)) sessionLost();
              else setErr(errorMessage(t, e));
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        {t("maint.reset_button")}
      </button>
    </MaintenanceDialog>
  );
}

function RollbackDialog({ version, discards, onClose, onDone, pin, t }: { version: number; discards: { customers: number; settings: number; users: number }; onClose: () => void; onDone: () => Promise<void>; pin: PinGate; t: T }) {
  const { source } = useApp();
  const { sessionLost } = useSession();
  const [totp, setTotp] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <MaintenanceDialog title={t("maint.rollback")} onClose={onClose} testId="maint-rollback-dialog">
      <div className={NOTE}>{t("maint.rollback_note", { n: version })}</div>
      {discards.customers + discards.settings + discards.users > 0 && (
        <p className="text-[11px] font-bold text-rose-600 leading-relaxed" data-testid="maint-rollback-discards">
          {t("maint.rollback_discards", discards)}
        </p>
      )}
      <TotpField value={totp} onChange={setTotp} t={t} />
      <Err text={err} />
      <button
        type="button"
        className={PRIMARY}
        disabled={busy || totp.length !== 6}
        onClick={() => {
          setBusy(true);
          setErr(null);
          void (async () => {
            try {
              const r = await pin.withPin((p) => source.datasetRollback(version, totp, p.pin!), { message: t("maint.rollback_pin") });
              if (!r) return;
              await onDone();
              onClose();
            } catch (e) {
              if (isSessionLost(e)) sessionLost();
              else setErr(errorMessage(t, e));
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        {t("maint.rollback")}
      </button>
    </MaintenanceDialog>
  );
}
